import { CircleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { RankBadge } from "@/components/RankBadge";
import { cn } from "@/lib/utils";

import { type ConditionSource, conditionOf } from "./condition";

/** Rank A–D / "PSA 10" / "未開封" — and a red "Rank needed" chip for a raw card without one. */
export function ConditionBadge({
  source,
  size = "md",
  className,
}: {
  source: ConditionSource;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const { t } = useTranslation("products");
  const condition = conditionOf(source);
  switch (condition.kind) {
    case "rank":
      return (
        <RankBadge
          rank={condition.rank}
          damaged={condition.damaged}
          size={size}
          className={className}
        />
      );
    case "rank_missing":
      return (
        <span
          data-slot="rank-missing"
          className={cn(
            "inline-flex items-center gap-1 rounded-full border border-[#b91c1c] bg-[#fef2f2] px-2 py-0.5 text-xs font-semibold whitespace-nowrap text-[#991b1b]",
            className,
          )}
        >
          <CircleAlert className="size-3.5" aria-hidden />
          {t("condition.rank_missing")}
        </span>
      );
    case "graded":
      return (
        <span
          data-slot="grade-label"
          className={cn(
            "inline-flex items-center rounded-sm border border-foreground px-1.5 text-xs font-bold whitespace-nowrap",
            className,
          )}
        >
          {condition.label}
        </span>
      );
    case "sealed":
      return (
        <span
          data-slot="sealed-label"
          className={cn(
            "inline-flex items-center rounded-sm bg-muted px-1.5 text-xs font-semibold whitespace-nowrap",
            className,
          )}
        >
          {t("condition.sealed")}
        </span>
      );
    default:
      return null;
  }
}
